import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import OEIconCodes from '../../../lib/oe-icon-codes';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import {OEControlState} from '../../../lib/oe-types';
import retardUpdate from '../../../lib/react/update-retarder';
import OEColorHelper from '../../../lib/color/oe-color-helper';
import OEButton from '../../elements/controls/oe-button';
import OEIcon from '../../elements/oe-icon';
import {OEObjectModelValueEditHelper} from '../../elements/object-model-value-edit/oe-object-model-value-edit-model';
import OEColor from '../../color-picker/oe-color';
import {OEColorPickerNumericInputType, OEColorPickerButton} from '../../color-picker/oe-color-picker';
import OEPopover from '../../views/oe-popover';
import {OENoteFunctionDefinitions} from './oe-note-function-model';
import {OEFunctionFragmentParameterModel} from './function/oe-function-model';
import {OEFunctionController} from './function/oe-function-controller';

export class OENoteFunctionController extends React.PureComponent {

    constructor(props) {
        super(props);

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.id = typeof(this.props.id) === 'number' ? this.props.id : null;
        this.style = null;

        this.controlState = OEControlState.normal;

        this.defaultColors = {
            textColor:          {x: 0, y: 0, z: 0, w: 1},
            backgroundColor:    {x: 1, y: 1, z: 1, w: 1},
            borderColor:        {x: 0, y: 0, z: 0, w: 1},
            lineColor:          {x: 0, y: 0, z: 0, w: 1},
        };

        this.defaultStateColors = {
            textColor: {
                normal:         {x: 0, y: 0, z: 0, w: 1},
                values: {
                    2:          {x: 0, y: 0, z: 0, w: 0.6}
                }
            },
            backgroundColor: {
                normal:         {x: 1, y: 1, z: 1, w: 1},
                values: {
                    1:          {x: 0, y: 1, z: 0, w: 1},
                    2:          {x: 1, y: 1, z: 1, w: 0.6},
                    4:          {x: 0, y: 1, z: 0, w: 1},
                    16:         OEColorHelper.themeColor()
                }
            },
            borderColor: {
                normal:         {x: 0, y: 0, z: 0, w: 1},
                values: {
                    2:          {x: 0, y: 0, z: 0, w: 0.6}
                }
            },
            lineColor: {
                normal:         {x: 0, y: 0, z: 0, w: 1},
                values: {
                    2:          {x: 0, y: 0, z: 0, w: 0.6}
                }
            }
        };

        this.state = {
            id:                 this.id,
            controlState:       this.controlState,
            colors:             this.defaultColors,
            stateColors:        this.defaultStateColors,
            func:               undefined
        };

        this.onNoteStyleChanged = this.onNoteStyleChanged.bind(this);
        this.onFunctionChanged = this.onFunctionChanged.bind(this);

        this.onTextColorBtnChange = this.onTextColorBtnChange.bind(this);
        this.onBorderColorBtnChange = this.onBorderColorBtnChange.bind(this);
        this.onLineColorBtnChange = this.onLineColorBtnChange.bind(this);
        this.onBorderColorBtnChange = this.onBorderColorBtnChange.bind(this);
        this.onBackgroundColorBtnChange = this.onBackgroundColorBtnChange.bind(this);
        this.onStateButtonPressed = this.onStateButtonPressed.bind(this);
        this.onFunctionControllerChange = this.onFunctionControllerChange.bind(this);
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.noteStyleChanged, this.onNoteStyleChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.noteFunctionChanged, this.onFunctionChanged);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.noteStyleChanged, this.onNoteStyleChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.noteFunctionChanged, this.onFunctionChanged);
    }

    componentWillReceiveProps(nextProps)    {
        if(nextProps.id != this.id)   {
            this.updateID(nextProps.id);
        }
    }

    updateID(id)    {
        if(typeof(id) !== 'number')  id = null;
        if(this.id === id)   return;
        this.id = id;
        this.setState({id: this.id});
        this.updateStyle();
        this.updateFunction();
    }

    updateControlState(state)   {
        if(this.controlState === state)    return;
        this.controlState = state;
        this.setState({controlState: this.controlState});
        this.updateColors();
    }

    valueForState(stateValue, state, defValue) {
        if(!stateValue) return defValue;
        return OEControlState.normal ? stateValue.normal : (stateValue.values && stateValue.values[state] ? stateValue.values[state] : defValue);
    }

    appliedValueForState(stateValue, state) {
        if(!stateValue) return undefined;
        let ret = this.valueForState(stateValue, state);

        if(!ret && (state & OEControlState.active)) {
            state = OEToolbox.binaryRemove(state, OEControlState.active);
            ret = this.valueForState(stateValue, state);
        }

        return ret ? ret : stateValue.normal;
    }

    updateColors()  {
        let colors = {};
        let stateColors = {};
        if(this.style)  {
            colors.textColor = this.appliedValueForState(this.style.textStateColors, this.controlState);
            colors.backgroundColor = this.appliedValueForState(this.style.backgroundStateColors, this.controlState);
            colors.borderColor = this.appliedValueForState(this.style.borderStateColors, this.controlState);
            colors.lineColor = this.appliedValueForState(this.style.lineStateColors, this.controlState);

            stateColors.textColor = this.style.textStateColors;
            stateColors.backgroundColor = this.style.backgroundStateColors;
            stateColors.borderColor = this.style.borderStateColors;
            stateColors.lineColor = this.style.lineStateColors;
        }

        colors.textColor = colors.textColor || this.defaultColors.textColor;
        colors.backgroundColor = colors.backgroundColor || this.defaultColors.backgroundColor;
        colors.borderColor = colors.borderColor || this.defaultColors.borderColor;
        colors.lineColor = colors.lineColor || this.defaultColors.lineColor;

        stateColors.textColor = stateColors.textColor || this.defaultStateColors.textColor;
        stateColors.backgroundColor = stateColors.backgroundColor || this.defaultStateColors.backgroundColor;
        stateColors.borderColor = stateColors.borderColor || this.defaultStateColors.borderColor;
        stateColors.lineColor = stateColors.lineColor || this.defaultStateColors.lineColor;

        this.setState({
            colors:             colors,
            stateColors:        stateColors
        });
    }

    updateStyle(style)  {
        if(!style && (this.oe.isReady() && this.id !== null))  style = this.oe.sharedInterface.getUIControllerNote().getNoteStyle(this.id);
        let oldStyle = this.style;
        this.style = style ? style : null;
        if(OEToolbox.shallowEqual(this.style, oldStyle, true))    return;
        this.updateColors();
    }

    updateFunction()    {
        let func = this.oe.isReady() && this.id !== null ? this.oe.sharedInterface.getUIControllerNote().getNoteFunction(this.id) : undefined;
        this.setState({func: func});
    }

    onNoteStyleChanged(message, userInfo)   {
        if(userInfo.ID !== this.id)  return;
        this.updateStyle(userInfo.style);
    }

    onFunctionChanged(message, userInfo) {
        if(userInfo.ID !== this.id)  return;
        this.updateFunction();
    }

    updateState(released)   {
        retardUpdate(this, () => {
            if(!this.oe.isReady() || released === true)   {
                this.style = null;
                this.setState({
                    colors:         this.defaultColors,
                    stateColors:    this.defaultStateColors,
                    func:           undefined
                });
                return;
            }

            this.updateStyle();
            this.updateFunction();
        });
    }

    render() {
        return  (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                <OEFunctionController
                    {...this.props}
                    headerClassName="widget-header-small"
                    headerSeparator={true}
                    addFragmentMenuTypes={OENoteFunctionDefinitions.addFragmentMenuTypes}
                    func={this.state.func}
                    onChange={this.onFunctionControllerChange}
                >
                    <div className="state-color-bar std-label-text-color">

                        <div className="color-btns">
                            <OEColorPickerButton
                                moduleId={this.props.moduleId}
                                className="transparent-btn"
                                boundariesElement={this.props.boundariesElement}
                                noHeader={true}
                                backdrop={true}
                                backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                                color={this.state.colors.textColor}
                                defaultColor={this.valueForState(this.defaultStateColors.textColor, this.state.controlState, this.state.stateColors.textColor.normal)}
                                hasAlphaControl={true}
                                numericInput={this.props.devFeatures ? OEColorPickerNumericInputType.textField : OEColorPickerNumericInputType.rgbInput}
                                hasPaletteButton={this.props.devFeatures}
                                onChange={this.onTextColorBtnChange}
                            >
                                <OEIcon code={OEIconCodes.noteTextColor}/>

                                <OEIcon className="color-bar" style={{color: OEColor.toDOMStr(this.state.colors.textColor)}} code={OEIconCodes.noteShowColor}/>
                            </OEColorPickerButton>

                            <OEColorPickerButton
                                moduleId={this.props.moduleId}
                                className="transparent-btn"
                                boundariesElement={this.props.boundariesElement}
                                noHeader={true}
                                backdrop={true}
                                backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                                color={this.state.colors.borderColor}
                                defaultColor={this.valueForState(this.defaultStateColors.borderColor, this.state.controlState, this.state.stateColors.borderColor.normal)}
                                hasAlphaControl={true}
                                numericInput={this.props.devFeatures ? OEColorPickerNumericInputType.textField : OEColorPickerNumericInputType.rgbInput}
                                hasPaletteButton={this.props.devFeatures}
                                onChange={this.onBorderColorBtnChange}
                            >
                                <OEIcon className="primary" code={OEIconCodes.noteBorderColor1}/>
                                <OEIcon className="secondary" code={OEIconCodes.noteBorderColor2}/>

                                <OEIcon className="color-bar" style={{color: OEColor.toDOMStr(this.state.colors.borderColor)}} code={OEIconCodes.noteShowColor}/>
                            </OEColorPickerButton>

                            <OEColorPickerButton
                                moduleId={this.props.moduleId}
                                className="transparent-btn"
                                boundariesElement={this.props.boundariesElement}
                                noHeader={true}
                                backdrop={true}
                                backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                                color={this.state.colors.lineColor}
                                defaultColor={this.valueForState(this.defaultStateColors.lineColor, this.state.controlState, this.state.stateColors.lineColor.normal)}
                                hasAlphaControl={true}
                                numericInput={this.props.devFeatures ? OEColorPickerNumericInputType.textField : OEColorPickerNumericInputType.rgbInput}
                                hasPaletteButton={this.props.devFeatures}
                                onChange={this.onLineColorBtnChange}
                            >
                                <OEIcon className="primary note-line-color" code={OEIconCodes.noteLineColor}/>
                                <OEIcon className="secondary note-line-color" code="|"/>

                                <OEIcon className="color-bar" style={{color: OEColor.toDOMStr(this.state.colors.lineColor)}} code={OEIconCodes.noteShowColor}/>
                            </OEColorPickerButton>

                            <OEColorPickerButton
                                moduleId={this.props.moduleId}
                                className="transparent-btn"
                                boundariesElement={this.props.boundariesElement}
                                noHeader={true}
                                backdrop={true}
                                backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                                color={this.state.colors.backgroundColor}
                                defaultColor={this.valueForState(this.defaultStateColors.backgroundColor, this.state.controlState, this.state.stateColors.backgroundColor.normal)}
                                hasAlphaControl={true}
                                numericInput={this.props.devFeatures ? OEColorPickerNumericInputType.textField : OEColorPickerNumericInputType.rgbInput}
                                hasPaletteButton={this.props.devFeatures}
                                onChange={this.onBackgroundColorBtnChange}
                            >
                                <OEIcon code={OEIconCodes.noteBackgroundColor}/>

                                <OEIcon className="color-bar" style={{color: OEColor.toDOMStr(this.state.colors.backgroundColor)}} code={OEIconCodes.noteShowColor}/>
                            </OEColorPickerButton>
                        </div>

                        <div className="state-btns">
                            <OEButton userData={OEControlState.highlighted} activated={!!(this.state.controlState & OEControlState.highlighted)} onPressed={this.onStateButtonPressed}>
                                :high
                            </OEButton>
                            <OEButton userData={OEControlState.selected} activated={!!(this.state.controlState & OEControlState.selected)} onPressed={this.onStateButtonPressed}>
                                :sel
                            </OEButton>
                            <OEButton userData={OEControlState.disabled} activated={!!(this.state.controlState & OEControlState.disabled)} onPressed={this.onStateButtonPressed}>
                                :dis
                            </OEButton>
                            <OEButton userData={OEControlState.active} activated={!!(this.state.controlState & OEControlState.active)} onPressed={this.onStateButtonPressed}>
                                :act
                            </OEButton>
                        </div>

                    </div>
                </OEFunctionController>
            </React.Fragment>
        );
    }

    onStateButtonPressed(e, sender) {
        let controlState = sender.props.userData;

        if(controlState !== OEControlState.active)  {
            let active = !!(this.state.controlState & OEControlState.active);
            if(this.state.controlState & controlState)    controlState = OEControlState.normal;
            if(active)  controlState = controlState | OEControlState.active;
        } else {
            controlState = OEToolbox.binaryAddRemove(this.state.controlState, controlState, !!(this.state.controlState & controlState));
        }
        
        this.updateControlState(controlState);
    }

    onFunctionControllerChange(sender, func_)    {
        if(!this.oe.isReady() || this.id === null)  return;
        let func = clone(func_);
        if(func && func.fragments)  {
            func.fragments.forEach(fragment => {
                if(fragment.params) fragment.params.$__model__ = OEObjectModelValueEditHelper.modelToCoreDictModel(OEFunctionFragmentParameterModel.modelForType(fragment.type));
            });
        }

        this.oe.sharedInterface.getUIControllerNote().setNoteFunction(this.id, func);
    }

    onTextColorBtnChange(sender, color, colorHSV, isDefault)	{
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerNote().setNoteTextStateColor(this.id, !isDefault ? color : this.valueForState(this.defaultStateColors.textColor, this.state.controlState), this.state.controlState);
    }

    onBorderColorBtnChange(sender, color, colorHSV, isDefault)	{
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerNote().setNoteBorderStateColor(this.id, !isDefault ? color : this.valueForState(this.defaultStateColors.borderColor, this.state.controlState), this.state.controlState);
    }

    onLineColorBtnChange(sender, color, colorHSV, isDefault)	{
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerNote().setNoteLineStateColor(this.id, !isDefault ? color : this.valueForState(this.defaultStateColors.lineColor, this.state.controlState), this.state.controlState);
    }

    onBackgroundColorBtnChange(sender, color, colorHSV, isDefault)	{
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerNote().setNoteBackgroundStateColor(this.id, !isDefault ? color : this.valueForState(this.defaultStateColors.backgroundColor, this.state.controlState), this.state.controlState);
    }
}

OENoteFunctionController.defaultProps = Object.assign({}, OEFunctionController.defaultProps, {
    className:          'note-function-controller'
});

OENoteFunctionController.propTypes = {
    moduleId:           PropTypes.string,
    className:          PropTypes.string,
    icon:               PropTypes.string,
    title:              PropTypes.string,
    titleId:            PropTypes.string,
    headerSeparator:    PropTypes.bool,
    onToggle:           PropTypes.func,
    id:                 PropTypes.number
};

//
export const OENoteFunctionPopoverController = OEPopover.coat(OENoteFunctionController, {
    noHeader:           true,
}, {
    placement:          'right',
    buttonClassName:    'transparent-btn',
});